// Public customer review submission page — /review/:token
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Star } from "lucide-react";
import { API, useSettings } from "../lib/providers";

const TENNIS_GREEN = "#a7ff00";

async function uploadImage(file, token) {
  // Convert to base64 then POST to /api/reviews/upload-photo (token-protected)
  const b64 = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const { data } = await axios.post(`${API}/reviews/upload-photo`, { token, file_b64: b64 });
  return data.url;
}

export default function ReviewPage() {
  const { token } = useParams();
  const settings = useSettings();
  const [order, setOrder] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/order/${token}`);
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Lien invalide");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) { setError("Choisissez une note"); return; }
    setSubmitting(true); setError("");
    try {
      await axios.post(`${API}/reviews/submit`, {
        token,
        rating,
        comment,
        photos,
      });
      setSubmitted(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSubmitting(false); }
  };

  const uploadPhoto = async (file) => {
    if (photos.length >= 6) return;
    try {
      const url = await uploadImage(file, token);
      setPhotos((p) => [...p, url]);
    } catch (e) {
      setError("Erreur upload photo");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white">Chargement…</div>;
  if (error && !order) return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="text-3xl font-black mb-3">Lien invalide</h1>
        <p className="text-white/60">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center px-4" data-testid="review-thankyou">
      <div className="max-w-md text-center space-y-4">
        <div className="text-6xl">🎾</div>
        <h1 className="text-3xl font-black">Merci !</h1>
        <p className="text-white/70">Votre avis a bien été enregistré. Il sera publié après validation.</p>
        <a href="/" className="inline-block mt-4 px-6 py-3 bg-neon text-black font-black uppercase tracking-widest text-xs">Retour à la boutique</a>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="review-page">
      <form onSubmit={submit} className="max-w-xl w-full space-y-6">
        <div className="text-center">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Votre avis compte !</h1>
          <p className="text-white/60 text-sm mt-2">
            Commande <strong>#{order.order_number}</strong> · {order.customer_name}
          </p>
          {order.items && order.items.length > 0 && (
            <div className="text-xs text-white/40 mt-1">
              {order.items.map((it, i) => <span key={i}>{it.name}{i < order.items.length - 1 ? " · " : ""}</span>)}
            </div>
          )}
        </div>

        {/* Stars */}
        <div className="border border-white/10 p-6 text-center">
          <label className="block text-xs uppercase tracking-widest text-white/60 mb-3">Votre note</label>
          <div className="flex justify-center gap-2" data-testid="review-stars-input">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setRating(n)}
                className="transition-transform hover:scale-110"
                data-testid={`review-star-${n}`}
              >
                <Star className="w-10 h-10" fill={n <= rating ? TENNIS_GREEN : "none"} color={n <= rating ? TENNIS_GREEN : "#666"} />
              </button>
            ))}
          </div>
          <div className="text-xs text-white/40 mt-2">{rating > 0 ? `${rating} / 5` : "Cliquez sur les étoiles"}</div>
        </div>

        {/* Comment */}
        <div>
          <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Votre commentaire (optionnel)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            placeholder="Partagez votre expérience…"
            className="w-full bg-black/40 border border-white/20 px-3 py-2 text-sm"
            data-testid="review-comment"
          />
        </div>

        {/* Photos */}
        <div>
          <label className="block text-xs uppercase tracking-widest text-white/60 mb-2">Photos (jusqu&apos;à 6, optionnel)</label>
          <div className="flex gap-2 flex-wrap">
            {photos.map((url, i) => (
              <div key={i} className="relative">
                <img src={url} alt="" className="w-20 h-20 object-cover border border-white/20" />
                <button type="button" onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-clay text-white w-5 h-5 text-xs font-bold rounded-full">×</button>
              </div>
            ))}
            {photos.length < 6 && (
              <label className="w-20 h-20 border-2 border-dashed border-white/20 hover:border-neon flex items-center justify-center cursor-pointer" data-testid="review-photo-upload">
                <span className="text-2xl text-white/40">+</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
              </label>
            )}
          </div>
        </div>

        {error && <div className="text-clay text-sm">{error}</div>}

        <button
          type="submit"
          disabled={submitting || rating < 1}
          className="w-full py-4 bg-neon text-black font-black uppercase tracking-widest text-sm disabled:opacity-30"
          data-testid="review-submit"
        >
          {submitting ? "Envoi…" : "Envoyer mon avis"}
        </button>
      </form>
    </div>
  );
}
